import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { cn } from "../../utils/cn";
import { CommentSidebar } from "./CommentSidebar";
import { CommentForm } from "./CommentForm";
import { CommentMarker } from "./CommentMarker";
import { CommentCursor } from "./CommentCursor";
import { CommentModeIndicator } from "./CommentModeIndicator";
import { AuthModal } from "../Auth/AuthModal";
import { SubscriptionModal } from "../Subscription/SubscriptionModal";
import { ShareProjectModal } from "../Shared/ShareProjectModal";
import { ToolbarButtons } from "./components/ToolbarButtons";
import { ToolbarMenu } from "./components/ToolbarMenu";
import { useCommentStore } from "../../store/commentStore";
import { useAuthStore } from "../../store/authStore";
import { useSubscriptionStore } from "../../store/subscriptionStore";
import { useToast } from "../../hooks/useToast";
import { useCommentMode } from "./hooks/useCommentMode";
import { useProjectData } from "./hooks/useProjectData";
import { CreditCardIcon } from "@heroicons/react/24/outline";
import { supabase } from "../../lib/supabase";

interface FloatingToolbarProps {
  projectId: string;
}

interface ToolbarMenuProps {
  user: any;
  isGuest: boolean;
  onViewProject: () => void;
  onManageSubscription: () => void;
  onShare: () => Promise<void>;
  onLearnMore: () => void;
  onSignOut: () => Promise<void>;
  menuItems?: Array<{
    name: string;
    onClick: () => void;
    icon: any;
    show: boolean;
  }>;
}

export const FloatingToolbar: React.FC<FloatingToolbarProps> = ({
  projectId,
}) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [isSubscriptionModalOpen, setIsSubscriptionModalOpen] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const isInitializedRef = useRef(false);
  const preservationIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const [highlightedCommentId, setHighlightedCommentId] = useState<
    string | null
  >(null);

  const navigate = useNavigate();
  const { user, isGuest, signOut } = useAuthStore();
  const { isSubscribed, isTrialActive, checkSubscriptionStatus } =
    useSubscriptionStore();
  const {
    comments,
    fetchComments,
    addComment,
    addReply,
    resolveComment,
    deleteComment,
    markAsRead,
  } = useCommentStore();
  const { showToast } = useToast();
  const { projectData, setProjectData, fetchProjectData } = useProjectData();
  const {
    isCommentMode,
    setIsCommentMode,
    commentFormPosition,
    setCommentFormPosition,
    cursorPosition,
    handleMouseMove,
    handlePageClick,
  } = useCommentMode();

  const guestPassword = projectId;

  // Bulletproof URL preservation system
  const bulletproofUrlPreservation = () => {
    if (isInitializedRef.current) return;

    console.log(
      "🚀 Initializing bulletproof URL preservation for FeedbackFlow widget"
    );

    // Function to ensure widget mode is always preserved
    const preserveWidgetMode = () => {
      try {
        const currentUrl = new URL(window.location.href);

        // Only preserve if we have the widget indicator and mode is missing
        if (
          !currentUrl.searchParams.has("mode") &&
          document.getElementById("feedbackflow-toolbar")
        ) {
          currentUrl.searchParams.set("mode", "widget");
          window.history.replaceState({}, "", currentUrl.toString());
          console.log(
            "✅ Widget mode preserved in URL:",
            currentUrl.toString()
          );
        }
      } catch (error) {
        console.warn("FeedbackFlow: URL preservation error:", error);
      }
    };

    // Store original navigation methods
    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;

    // Override pushState to always preserve widget mode
    window.history.pushState = function (
      state: any,
      title: string,
      url?: string | URL | null
    ) {
      if (url && document.getElementById("feedbackflow-toolbar")) {
        try {
          const newUrl = new URL(url, window.location.origin);
          if (!newUrl.searchParams.has("mode")) {
            newUrl.searchParams.set("mode", "widget");
            url = newUrl.toString();
          }
        } catch (e) {
          console.warn(
            "FeedbackFlow: Failed to preserve mode in pushState:",
            e
          );
        }
      }
      return originalPushState.call(this, state, title, url);
    };

    // Override replaceState to always preserve widget mode
    window.history.replaceState = function (
      state: any,
      title: string,
      url?: string | URL | null
    ) {
      if (url && document.getElementById("feedbackflow-toolbar")) {
        try {
          const newUrl = new URL(url, window.location.origin);
          if (!newUrl.searchParams.has("mode")) {
            newUrl.searchParams.set("mode", "widget");
            url = newUrl.toString();
          }
        } catch (e) {
          console.warn(
            "FeedbackFlow: Failed to preserve mode in replaceState:",
            e
          );
        }
      }
      return originalReplaceState.call(this, state, title, url);
    };

    // Handle popstate events
    const handlePopState = () => {
      setTimeout(preserveWidgetMode, 0);
    };

    // Handle link clicks to add mode=widget to internal links
    const handleLinkClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const link = target.closest("a") as HTMLAnchorElement;

      if (
        link &&
        link.href &&
        !link.href.startsWith("javascript:") &&
        !link.href.startsWith("#") &&
        !link.target
      ) {
        try {
          const linkUrl = new URL(link.href);
          const currentUrl = new URL(window.location.href);

          // If it's same origin and doesn't have mode, add it
          if (
            linkUrl.origin === currentUrl.origin &&
            !linkUrl.searchParams.has("mode")
          ) {
            linkUrl.searchParams.set("mode", "widget");
            link.href = linkUrl.toString();
            console.log("🔗 Added widget mode to link:", link.href);
          }
        } catch (error) {
          // Ignore invalid URLs
        }
      }
    };

    // Set up event listeners
    window.addEventListener("popstate", handlePopState);
    window.addEventListener("hashchange", preserveWidgetMode);
    document.addEventListener("click", handleLinkClick, true); // Use capture phase

    // Aggressive preservation - check every second
    preservationIntervalRef.current = setInterval(preserveWidgetMode, 1000);

    // Initial preservation
    preserveWidgetMode();

    isInitializedRef.current = true;

    console.log("✅ Bulletproof URL preservation system activated");

    // Return cleanup function
    return () => {
      window.history.pushState = originalPushState;
      window.history.replaceState = originalReplaceState;
      window.removeEventListener("popstate", handlePopState);
      window.removeEventListener("hashchange", preserveWidgetMode);
      document.removeEventListener("click", handleLinkClick, true);

      if (preservationIntervalRef.current) {
        clearInterval(preservationIntervalRef.current);
        preservationIntervalRef.current = null;
      }

      isInitializedRef.current = false;
      console.log("🧹 URL preservation system cleaned up");
    };
  };

  // Initialize bulletproof URL preservation on mount
  useEffect(() => {
    const cleanup = bulletproofUrlPreservation();

    return cleanup;
  }, []);

  // Check subscription status on mount and when user changes
  useEffect(() => {
    if (user?.id) {
      checkSubscriptionStatus(user.id);
    }
  }, [user?.id, checkSubscriptionStatus]);

  // Fetch comments when user is authenticated (user or guest)
  useEffect(() => {
    const fetchCommentsForProject = async () => {
      if (!guestPassword || (!user && !isGuest)) return;

      try {
        // Fetch ALL comments for the project (not just current page)
        await fetchComments(guestPassword);
      } catch (error) {
        console.error("Error fetching comments:", error);
      }
    };

    fetchCommentsForProject();
  }, [guestPassword, fetchComments, user, isGuest]);

  // No need to re-fetch comments on navigation since we show all comments in sidebar

  // Get all comments for sidebar (not just current page)
  const allComments = comments.filter((comment) => {
    try {
      // Just make sure the comment has valid position data
      const position =
        typeof comment.position === "string"
          ? JSON.parse(comment.position)
          : comment.position;
      return position?.pageUrl; // Has a valid pageUrl
    } catch {
      return false; // Skip malformed comments
    }
  });

  // Filter comments for current page (only for markers on canvas)
  const currentPageComments = comments.filter((comment) => {
    try {
      const position =
        typeof comment.position === "string"
          ? JSON.parse(comment.position)
          : comment.position;
      return position?.pageUrl === window.location.href;
    } catch {
      return false; // Skip malformed comments
    }
  });

  // Filter out resolved comments for display on canvas
  const activeComments = currentPageComments.filter(
    (comment) => comment.status !== "resolved"
  );

  const handleShare = async () => {
    if (!user) {
      setIsAuthModalOpen(true);
      return;
    }

    try {
      // Get the current project data using the guest password
      const { data, error } = await supabase
        .from("projects")
        .select("*")
        .eq("guest_password", projectId)
        .single();

      if (error) throw error;
      if (!data) throw new Error("Project not found");

      // Set the project data with the actual website URL
      setProjectData(data);
      setIsShareModalOpen(true);
    } catch (error) {
      console.error("Share error:", error);
      showToast("Failed to share project. Please try again.", "error");
    }
  };

  const handleCommentSubmit = async (text: string, screenshotBlob?: Blob) => {
    if (!isSubscribed && !isTrialActive && !isGuest) {
      setIsSubscriptionModalOpen(true);
      return;
    }

    if (commentFormPosition && (user || isGuest)) {
      try {
        await addComment(
          guestPassword,
          {
            text,
            author: user?.email || "Guest",
            position: commentFormPosition,
            timestamp: Date.now(),
          },
          screenshotBlob
        );
        setCommentFormPosition(null);
        showToast("Comment added successfully", "success");
      } catch (error) {
        showToast("Failed to add comment", "error");
      }
    }
  };

  const handleReply = async (commentId: string, text: string) => {
    if (!isSubscribed && !isTrialActive && !isGuest) {
      setIsSubscriptionModalOpen(true);
      return;
    }

    try {
      await addReply(commentId, {
        text,
        author: user?.email || "Guest",
        timestamp: Date.now(),
      });
      await markAsRead(commentId);
      showToast("Reply added successfully", "success");
    } catch (error) {
      showToast("Failed to add reply", "error");
    }
  };

  const handleCommentClick = (commentId?: string) => {
    if (user || isGuest) {
      setIsSidebarOpen(!isSidebarOpen);
    } else {
      setIsAuthModalOpen(true);
    }
  };

  const handleLearnMore = () => {
    navigate("/?mode=dashboard&section=support", { replace: true });
  };

  const handleManageSubscription = () => {
    navigate("/?mode=dashboard&section=billing", { replace: true });
  };

  const handleViewProject = () => {
    navigate("/?mode=dashboard&section=projects", { replace: true });
  };

  const handleAddComment = async (position: any) => {
    if (!guestPassword) {
      console.error("Invalid project ID");
      return;
    }
    await addComment(guestPassword, position);
  };

  // Auto-scroll to comment if URL has comment parameter
  useEffect(() => {
    if (isCommentMode && comments.length > 0) {
      const urlParams = new URLSearchParams(window.location.search);
      const commentId = urlParams.get("comment");

      if (commentId) {
        const targetComment = comments.find((c) => c.id === commentId);
        if (
          targetComment &&
          targetComment.position?.pageUrl === window.location.href
        ) {
          // Use the same logic as the "Go to Location" button
          setTimeout(() => {
            if (targetComment.position?.isFixedElement) {
              // Fixed element - just highlight without scrolling
              setHighlightedCommentId(commentId);
              setTimeout(() => setHighlightedCommentId(null), 3000);
            } else {
              // Regular element - scroll and highlight
              window.scrollTo({
                left:
                  (targetComment.position?.pageX || 0) - window.innerWidth / 2,
                top:
                  (targetComment.position?.pageY || 0) - window.innerHeight / 2,
                behavior: "smooth",
              });
              setHighlightedCommentId(commentId);
              setTimeout(() => setHighlightedCommentId(null), 3000);
            }

            // Open sidebar after highlighting
            setTimeout(() => {
              setIsSidebarOpen(true);
            }, 1000);
          }, 500);
        }
      }
    }
  }, [isCommentMode, comments]);

  // Handle close sidebar and highlight comment
  useEffect(() => {
    const handleCloseSidebarAndHighlight = (event: CustomEvent) => {
      const { commentId, shouldScroll, x, y } = event.detail;

      // Close sidebar first
      setIsSidebarOpen(false);

      // Wait for sidebar to close, then scroll and highlight
      setTimeout(() => {
        if (shouldScroll) {
          window.scrollTo({
            left: x - window.innerWidth / 2,
            top: y - window.innerHeight / 2,
            behavior: "smooth",
          });
        }

        // Highlight the comment marker
        setHighlightedCommentId(commentId);

        // Remove highlight after 3 seconds
        setTimeout(() => {
          setHighlightedCommentId(null);
        }, 3000);
      }, 300); // Wait for sidebar close animation
    };

    window.addEventListener(
      "feedbackflow:close-sidebar-and-highlight",
      handleCloseSidebarAndHighlight as EventListener
    );

    return () => {
      window.removeEventListener(
        "feedbackflow:close-sidebar-and-highlight",
        handleCloseSidebarAndHighlight as EventListener
      );
    };
  }, []);

  return (
    <>
      {isCommentMode && (
        <>
          {/* Main comment mode overlay - covers entire viewport with maximum z-index */}
          <div
            className="feedbackflow-comment-overlay fixed inset-0"
            style={{
              cursor: "crosshair",
              zIndex: 2147483643, // Just below sidebar but above everything else
              pointerEvents: "auto",
            }}
            onClick={handlePageClick}
            onMouseMove={handleMouseMove}
          />

          {/* Sidebar exclusion overlay - prevents commenting in sidebar area */}
          {isSidebarOpen && (
            <div
              className="fixed top-0 bottom-0 right-0 w-[320px]"
              style={{
                cursor: "default",
                zIndex: 2147483643,
                pointerEvents: "auto",
              }}
            />
          )}

          {!user && !isGuest ? (
            <div
              className="fixed inset-x-0 top-4 flex justify-center"
              style={{ zIndex: 2147483647 }}
            >
              <div
                className="bg-black bg-opacity-75 px-4 py-2 rounded-full text-sm font-medium"
                style={{ color: "#ffffff" }}
              >
                Sign in to add comments
              </div>
            </div>
          ) : (
            <CommentModeIndicator onDisable={() => setIsCommentMode(false)} />
          )}
          <CommentCursor
            style={
              {
                left: cursorPosition.x,
                top: cursorPosition.y,
                display:
                  isSidebarOpen && cursorPosition.x > window.innerWidth - 320
                    ? "none"
                    : "block",
              } as { left: number; top: number }
            }
          />
        </>
      )}

      {/* Only show active comments as markers on the canvas */}
      {activeComments.map((comment) => (
        <CommentMarker
          key={comment.id}
          comment={comment}
          onClick={() => handleCommentClick(comment.id)}
          isCommentMode={isCommentMode}
          activeTab="active"
          isHighlighted={highlightedCommentId === comment.id}
        />
      ))}

      {commentFormPosition && (
        <CommentForm
          position={commentFormPosition}
          onSubmit={handleCommentSubmit}
          onCancel={() => setCommentFormPosition(null)}
        />
      )}

      <CommentSidebar
        isOpen={isSidebarOpen}
        onClose={() => setIsSidebarOpen(false)}
        comments={allComments}
        isLoading={false}
        error={null}
        onReply={handleReply}
        onResolve={resolveComment}
        onDelete={deleteComment}
        onSignIn={() => setIsAuthModalOpen(true)}
      />

      <AuthModal
        isOpen={isAuthModalOpen}
        onClose={() => setIsAuthModalOpen(false)}
        defaultView="guest"
      />

      <SubscriptionModal
        isOpen={isSubscriptionModalOpen}
        onClose={() => setIsSubscriptionModalOpen(false)}
      />

      {projectData && (
        <ShareProjectModal
          isOpen={isShareModalOpen}
          onClose={() => {
            setIsShareModalOpen(false);
            setProjectData(null);
          }}
          project={projectData}
        />
      )}

      <div
        id="feedbackflow-toolbar"
        className={cn(
          "fixed bottom-4 bg-white rounded-full shadow-lg border border-gray-200 transition-all duration-300",
          "flex items-center",
          isCommentMode &&
            "ring-2 ring-[#9747FF]/30 shadow-[0_0_15px_rgba(151,71,255,0.4)]",
          isSidebarOpen ? "right-[336px]" : "right-4"
        )}
        style={{ zIndex: 2147483647 }} // High z-index to appear above everything
      >
        {/* Logo Section */}
        <div className="px-4 py-2 flex items-center">
          <svg
            width="24"
            height="25"
            viewBox="0 0 24 25"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <defs>
              <linearGradient
                id="blockGradient1"
                x1="0%"
                y1="0%"
                x2="100%"
                y2="100%"
              >
                <stop
                  offset="0%"
                  style={{ stopColor: "#6366F1", stopOpacity: 1 }}
                />
                <stop
                  offset="100%"
                  style={{ stopColor: "#8B5CF6", stopOpacity: 1 }}
                />
              </linearGradient>
              <linearGradient
                id="blockGradient2"
                x1="0%"
                y1="0%"
                x2="100%"
                y2="100%"
              >
                <stop
                  offset="0%"
                  style={{ stopColor: "#0EA5E9", stopOpacity: 1 }}
                />
                <stop
                  offset="100%"
                  style={{ stopColor: "#06B6D4", stopOpacity: 1 }}
                />
              </linearGradient>
              <linearGradient
                id="blockGradient3"
                x1="0%"
                y1="0%"
                x2="100%"
                y2="100%"
              >
                <stop
                  offset="0%"
                  style={{ stopColor: "#10B981", stopOpacity: 1 }}
                />
                <stop
                  offset="100%"
                  style={{ stopColor: "#34D399", stopOpacity: 1 }}
                />
              </linearGradient>
              <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
                <feDropShadow
                  dx="0"
                  dy="1"
                  stdDeviation="1"
                  floodColor="rgba(0,0,0,0.1)"
                />
              </filter>
            </defs>

            {/* Background circle */}
            <circle
              cx="12"
              cy="12.5"
              r="11"
              fill="white"
              stroke="#F1F5F9"
              strokeWidth="0.5"
            />

            {/* Building Blocks representing comments */}
            {/* Top block (first comment) */}
            <rect
              x="4"
              y="6"
              width="7"
              height="3"
              rx="1.5"
              fill="url(#blockGradient1)"
              filter="url(#shadow)"
            />
            <circle cx="5.5" cy="7.5" r="0.8" fill="white" opacity="0.9" />
            <rect
              x="6.8"
              y="7.2"
              width="3.2"
              height="0.6"
              rx="0.3"
              fill="white"
              opacity="0.7"
            />

            {/* Middle block (second comment) */}
            <rect
              x="6"
              y="10.5"
              width="8"
              height="3"
              rx="1.5"
              fill="url(#blockGradient2)"
              filter="url(#shadow)"
            />
            <circle cx="7.5" cy="12" r="0.8" fill="white" opacity="0.9" />
            <rect
              x="8.8"
              y="11.7"
              width="4.2"
              height="0.6"
              rx="0.3"
              fill="white"
              opacity="0.7"
            />

            {/* Bottom block (third comment) */}
            <rect
              x="8"
              y="15"
              width="6"
              height="3"
              rx="1.5"
              fill="url(#blockGradient3)"
              filter="url(#shadow)"
            />
            <circle cx="9.5" cy="16.5" r="0.8" fill="white" opacity="0.9" />
            <rect
              x="10.8"
              y="16.2"
              width="2.2"
              height="0.6"
              rx="0.3"
              fill="white"
              opacity="0.7"
            />

            {/* Comment bubble icon */}
            <path
              d="M16.5 8.5 C18.5 8.5 19.5 9.5 19.5 11 C19.5 12.5 18.5 13.5 16.5 13.5 L15.8 13.5 L15 15 L16 13.5 C14.5 13.5 14 12.5 14 11 C14 9.5 15 8.5 16.5 8.5 Z"
              fill="#F59E0B"
              stroke="white"
              strokeWidth="0.5"
              filter="url(#shadow)"
            />

            {/* Chat dots in bubble */}
            <circle cx="15.8" cy="11" r="0.4" fill="white" />
            <circle cx="16.5" cy="11" r="0.4" fill="white" />
            <circle cx="17.2" cy="11" r="0.4" fill="white" />
          </svg>
        </div>

        {/* Divider */}
        <div className="h-6 w-px bg-gray-200 my-auto" />

        {/* Comments and Actions Section */}
        <div className="px-2 py-2">
          <ToolbarButtons
            user={user}
            isGuest={isGuest}
            commentsCount={currentPageComments.length}
            isCommentMode={isCommentMode}
            isSidebarOpen={isSidebarOpen}
            onToggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)}
            onToggleCommentMode={() => {
              if (!user && !isGuest) {
                setIsAuthModalOpen(true);
              } else {
                setIsCommentMode(!isCommentMode);
              }
            }}
            onShare={handleShare}
          />
        </div>

        {/* Menu Section */}
        <div className="px-2 py-2">
          <ToolbarMenu
            user={user}
            isGuest={isGuest}
            onViewProject={handleViewProject}
            onManageSubscription={() => setIsSubscriptionModalOpen(true)}
            onShare={handleShare}
            onLearnMore={handleLearnMore}
            onSignOut={async () => {
              await signOut();
              setIsCommentMode(false);
              setCommentFormPosition(null);
            }}
          />
        </div>
      </div>
    </>
  );
};
